import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LogOut, Menu, UserCircle } from "lucide-react";
import { fetchHrAccess } from "../../api/HrAPIs";
import { useAuth } from "../../utils/auth";
import { canAccessPanel } from "../../utils/roles";
import { images } from "../../constants/images";
import NotificationBell from "../notifications/NotificationBell";
import AdminSidebar from "./AdminSidebar";
import { RoleBadge } from "./dashboardUi";

export default function AdminShell({ badges, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { authState, logout } = useAuth();
  const { data: hrAccess } = useQuery({
    queryKey: ["hrAccess"],
    queryFn: fetchHrAccess,
    enabled: canAccessPanel(authState.role),
    retry: false,
    staleTime: 60_000,
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(min-width: 1024px)");
    const onChange = () => {
      if (media.matches) setMobileOpen(false);
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const handleLogout = () => {
    setIsMenuOpen(false);
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen flex-row-reverse overflow-hidden bg-slate-50" dir="rtl">
      <AdminSidebar
        role={authState.role}
        permissions={authState.permissions}
        hrAccess={hrAccess}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        badges={badges}
      />

      {mobileOpen && (
        <button
          type="button"
          aria-label="إغلاق القائمة"
          className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="relative z-40 shrink-0 border-b border-slate-200/80 bg-white/90 px-3 py-2.5 backdrop-blur-xl md:px-5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2 md:gap-3">
              <img src={images.logo} alt="Girabatcom" className="h-9 object-contain md:h-10" />
              <div className="hidden h-8 w-px bg-slate-200 md:block" />
              <div className="hidden md:block">
                <RoleBadge role={authState.role} />
              </div>
              <NotificationBell />
              <div className="max-w-[140px] overflow-hidden md:hidden">
                <RoleBadge role={authState.role} size="sm" />
              </div>
              <div className="relative" style={{ zIndex: 9999 }}>
                <button
                  type="button"
                  onClick={() => setIsMenuOpen((open) => !open)}
                  className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  aria-label="قائمة المستخدم"
                  aria-expanded={isMenuOpen}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 shadow-sm ring-2 ring-white">
                    <UserCircle size={24} className="text-white" />
                  </div>
                </button>
                {isMenuOpen && (
                  <>
                    <div className="fixed inset-0" style={{ zIndex: 9998 }} onClick={() => setIsMenuOpen(false)} />
                    <div
                      className="absolute right-0 top-14 mt-2 w-64 overflow-hidden rounded-2xl border border-slate-200/60 bg-white/95 py-2 shadow-2xl backdrop-blur-lg"
                      style={{ zIndex: 9999 }}
                    >
                      <div className="border-b border-slate-100 px-5 py-3">
                        <p className="mb-1.5 text-xs text-slate-500">مسجل الدخول باسم</p>
                        <RoleBadge role={authState.role} size="sm" />
                      </div>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full items-center gap-3 px-5 py-3 text-red-600 transition-colors hover:bg-red-50"
                      >
                        <LogOut size={17} />
                        <span className="text-sm font-medium">تسجيل الخروج</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <button
              type="button"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100 lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="فتح القائمة"
              aria-expanded={mobileOpen}
              aria-controls="admin-sidebar"
            >
              <Menu size={20} />
            </button>
          </div>
        </header>

        <main
          className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 10%, rgba(99,102,241,0.10), transparent 40%), radial-gradient(circle at 80% 30%, rgba(217,70,239,0.08), transparent 45%), radial-gradient(circle at 50% 90%, rgba(20,184,166,0.07), transparent 40%)",
          }}
        >
          <div className="mx-auto max-w-7xl px-3 py-4 md:px-6 md:py-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
